import { createSimpleStore } from './factories/simpleStoreFactory';
import {
  GridSortModel,
  GridPaginationModel,
  GridFilterModel,
  GridColumnVisibilityModel,
  GridPinnedColumnFields,
  GridDensity
} from '@mui/x-data-grid-premium';

export type UserPreferences = {
  pageSize: number;
  page?: number;
  theme?: string;
  language?: string;
  timePickerShorthandEnabled?: boolean;
  density: GridDensity;
  [key: string]:
    | GridSortModel
    | GridFilterModel
    | GridPaginationModel
    | GridColumnVisibilityModel
    | GridPinnedColumnFields
    | string[]
    | Record<string, number>
    | number
    | string
    | boolean
    | undefined;
};

type StoredPreferences = {
  user_preferences: UserPreferences;
};

/** Preference keys for scheduler/calendar view modes (persisted per user). */
export const PREFERENCE_KEYS = {
  CALENDAR_VIEW_MODE: 'calendarViewMode',
  TIMELINE_VIEW_MODE: 'timelineViewMode',
  /**
   * When true, the global create FAB is shown on User Management routes.
   * Default false: FAB is hidden there until the user turns it on (saved in browser per user).
   */
  USER_MANAGEMENT_GLOBAL_FAB_VISIBLE: 'userManagementGlobalFabVisible'
} as const;

const DEFAULT_PREFERENCES: UserPreferences = {
  pageSize: 10,
  density: 'standard',
  [PREFERENCE_KEYS.CALENDAR_VIEW_MODE]: 'week',
  [PREFERENCE_KEYS.TIMELINE_VIEW_MODE]: 'day',
  [PREFERENCE_KEYS.USER_MANAGEMENT_GLOBAL_FAB_VISIBLE]: false
};

const DEFAULT_USER_ID = 'default_user';

function getStorageKey(): string {
  if (typeof window === 'undefined') {
    return DEFAULT_USER_ID;
  }

  const storedUserName = localStorage.getItem('userName');
  if (!storedUserName || storedUserName === 'null' || storedUserName === 'undefined') {
    return DEFAULT_USER_ID;
  }

  return storedUserName;
}

function loadPersistedPreferences(): UserPreferences {
  if (typeof window === 'undefined') {
    return DEFAULT_PREFERENCES;
  }

  const storageKey = getStorageKey();
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const parsed: StoredPreferences = JSON.parse(stored);
      if (parsed.user_preferences) {
        return { ...DEFAULT_PREFERENCES, ...parsed.user_preferences };
      }
    }
  } catch (error) {
    console.warn('Failed to parse stored preferences:', error);
  }

  return DEFAULT_PREFERENCES;
}

function persistPreferences(preferences: UserPreferences): void {
  if (typeof window === 'undefined') {
    return;
  }

  const storageKey = getStorageKey();
  try {
    const data: StoredPreferences = { user_preferences: preferences };
    localStorage.setItem(storageKey, JSON.stringify(data));
  } catch (error) {
    console.warn('Failed to save preferences to localStorage:', error);
  }
}

type UserPreferencesState = {
  preferences: UserPreferences;
};

type UserPreferencesActions = {
  setPreference: (key: string, value: UserPreferences[string]) => void;
  setAllPreferences: (newPrefs: UserPreferences) => void;
  logoutUser: () => void;
  getUserName: () => string | null;
};

export type UserPreferencesStore = UserPreferencesState & UserPreferencesActions;

export const useUserPreferencesStore = createSimpleStore<UserPreferencesState, UserPreferencesActions>({
  name: 'UserPreferencesStore',
  initialState: {
    preferences: loadPersistedPreferences()
  },
  actions: (set, get) => ({
    setPreference: (key: string, value: UserPreferences[string]) => {
      set((state) => {
        const updated = { ...state.preferences, [key]: value };
        persistPreferences(updated);
        return { preferences: updated };
      });
    },

    setAllPreferences: (newPrefs: UserPreferences) => {
      persistPreferences(newPrefs);
      set({ preferences: newPrefs });
    },

    logoutUser: () => {
      const state = get();
      const storageKey = getStorageKey();

      // Get the most current preferences from state
      const currentPreferences = state.preferences;

      // Save preferences to the username-based key
      if (currentPreferences && Object.keys(currentPreferences).length > 0) {
        try {
          const preferencesData = JSON.stringify({ user_preferences: currentPreferences });
          localStorage.setItem(storageKey, preferencesData);

          // Verify the write was successful
          const verification = localStorage.getItem(storageKey);
          if (!verification || verification !== preferencesData) {
            console.warn('localStorage write verification failed');
          }
        } catch (error) {
          console.warn('Failed to save preferences during logout:', error);
        }
      }

      // Remove username AFTER saving preferences
      try {
        localStorage.removeItem('userName');
      } catch (error) {
        console.warn('Failed to remove username during logout:', error);
      }
    },

    getUserName: () => {
      if (typeof window === 'undefined') {
        return null;
      }

      const storedUserName = localStorage.getItem('userName');
      if (!storedUserName || storedUserName === 'null' || storedUserName === 'undefined') {
        return null;
      }

      return storedUserName;
    }
  })
});
